import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { makeTownFolk } from '../entities/npc.js';
import { createAmbient } from '../entities/ambient.js';
import { HUD } from '../ui/hud.js';
import { getState, getCrumbs, spendCrumbs, upgradeBuilding, setFlag, getFlag } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady, objectiveProgress, QUESTS, flagObjective, isActive, isCompleted } from '../core/quests.js';
import { isDaytime, isNight, getPhase, isOpen, openLabel, getSeason } from '../core/world.js';
import { WeatherSystem, getWeatherState, weatherName, weatherIcon } from '../core/weather.js';

// The town square. Bread Boi walks around a central plaza ringed by buildings.
// Walking up to a building's door shows an "enter" prompt that swaps scenes.
// Buildings render at a visual level driven by the save file (build-it-up).
//
// Compass Plan: 8 exits on the hub, and the hub is now FULL. New zones branch off
// district hubs (Downtown, Uptown, Forest, Beach), never off the square.
//   Ground radius 48 · Plaza radius 11 · Building ring 17 · Gates 28.
//   Buildings sit on 22.5° offsets between roads; roads use one paving material.

const ROAD_STONE = 0xC9B896;
const GROUND_R = 48;
const PLAZA_R = 11;
const BUILD_R = 17;
const GATE_R = 28;

const BUILDINGS = [
  { id:'house',  name:"Bread Boi's House", scene:'house',  color:C.tangerine, angle:337.5, pos:new THREE.Vector3(-6.5, 0, -15.7), hours:'always' }, // N-by-NW
  { id:'comics', name:"Gary's Comics",     scene:'comics', color:C.raspberry, angle: 22.5, pos:new THREE.Vector3( 6.5, 0, -15.7), hours:'day'    }, // N-by-NE
  { id:'council',name:'City Council',      scene:'council',color:0xA89078,    angle: 67.5, pos:new THREE.Vector3(15.7, 0,  -6.5), hours:'day'    }, // E-by-NE
  { id:'court',  name:'Avocado Court',     scene:'court',  color:C.sprout,    angle:112.5, pos:new THREE.Vector3(15.7, 0,   6.5), hours:'day'    }, // E-by-SE
  { id:'grill',  name:"Big T's Grill",     scene:'grill',  color:C.crustDark, angle:157.5, pos:new THREE.Vector3( 6.5, 0,  15.7), hours:{ open: 10, close: 22 } }, // S-by-SE, food quarter
  { id:'cafe',   name:'Yogurt Café',       scene:'cafe',   color:C.cobalt,    angle:202.5, pos:new THREE.Vector3(-6.5, 0,  15.7), hours:'day'    }  // S-by-SW, food quarter
  // 247.5° and 292.5° are deliberately reserved for future buildings.
];

// Cost to raise a building from its current level to the next. Max level = 4.
const UPGRADE_COST = { 1: 50, 2: 150, 3: 300 };
const MAX_LEVEL = 4;

// Buildings whose interior is a shop and closes at night.
const SHOPS = new Set(['comics', 'cafe', 'clothing', 'furniture', 'restaurant', 'dojo', 'casino', 'garage', 'apartment', 'bank', 'sheriff', 'grill']);

export function createTownScene(){
  return new TownScene();
}

class TownScene {
  constructor(){
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(C.breeze);
    this.scene.fog = new THREE.Fog(C.breeze, 32, 66);

    this.camera = new THREE.PerspectiveCamera(48, window.innerWidth/window.innerHeight, 0.1, 160);
    this.camDist = 26;
    this.camTarget = new THREE.Vector3(0, 1.4, 0);
    this.azimuth = 0; this.polar = 0.5;

    this.doors = [];       // {building, doorPos, sign, group}
    this.nearTarget = null;
    this.panels = [];      // mayor/quest dialog panels
    this.mayorTab = 'quests';

    this._buildLights();
    this._buildGround();
    this._buildBuildings();
    this._buildDowntownRoad();
    this._buildForestRoad();
    this._buildFarmRoad();
    this._buildParkRoad();
    this._buildUptownRoad();
    this._buildToasterRoad();
    this._buildBeachRoad();
    this._buildBananaRoad();
    this._buildPlaza();       // fountain, statue, benches, lamps, planting
    this._buildPerimeter();   // tree line around the world edge
    this._buildTownFurniture();

    // pilgrimage statue hotspot (receives the daily blessing)
    this.statuePos = new THREE.Vector3(0, 0, 0);
    this._buildBakerKingStatue();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 8.5);
    this.scene.add(this.bread.root);

    // the neighbors — wander the plaza; filtered by day/night schedule
    this.npcs = makeTownFolk();
    this.npcs.forEach(n => {
      n.root.visible = n.shouldSpawn();
      this.scene.add(n.root);
    });

    // King Bread — the Baker King's spirit visits the square after his ceremony.
    this.kingBread = this.npcs.find(n => n.id === 'kingbread');
    if (this.kingBread){
      this.kingBread.root.position.set(7.5, 0, 0);
      this.kingBread.root.rotation.y = -Math.PI / 2;
      this.kingBread.root.visible = getFlag('bakerKingBlessing');
    }

    // Mayor Muffin — town quest-giver, on the plaza rim
    this.mayor = this._makeMayor();
    this.scene.add(this.mayor.root);

    // butterflies + birds over the plaza
    this.ambient = createAmbient({ radius: 40, butterflies: 6, birds: 4 });
    this.scene.add(this.ambient.group);

    // weather particles for the current season/weather
    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 0), radius: 48 });
    this._applyWeather();
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  _makeMayor(){
    const build = (g) => {
      const muffin = box(.72, .62, .72, 0xC9A876, 0, .82, 0, g);   // cake body
      const top = cyl(.38, .38, .22, 0x8B5A2B, 0, 1.24, 0, g, 16);  // muffin top
      const chip1 = sphere(.08, 0x4A3020, -.12, 1.34, .18, g, 8);
      const chip2 = sphere(.08, 0x4A3020,  .14, 1.30, -.08, g, 8);
      sphere(.07, 0x2E2018, -.16, 1.05, .34, g, 8);                // eyes
      sphere(.07, 0x2E2018,  .16, 1.05, .34, g, 8);
      box(.22, .06, .06, 0x2E2018, 0, .92, .36, g);                // smile
      cyl(.04, .04, .42, C.cream, -.42, .72, 0, g, 6);             // arms
      cyl(.04, .04, .42, C.cream,  .42, .72, 0, g, 6);
      cyl(.08, .08, .42, C.crustDark, -.16, .21, 0, g, 6);         // legs
      cyl(.08, .08, .42, C.crustDark,  .16, .21, 0, g, 6);
      const hat = cyl(.22, .26, .28, 0x3D8C5A, 0, 1.6, 0, g, 16);   // little mayor hat
      cyl(.26, .26, .04, 0x2C6A42, 0, 1.46, 0, g, 16);
    };
    return new Townsperson({
      name: 'Mayor Muffin', line: 'how do you like the town so far?',
      pos: new THREE.Vector3(-7.5, 0, 0), facing: Math.PI / 2,
      wander: 0, speed: 0, bubbleY: 2.6, build,
      giver: 'mayor'
    });
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.7));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.85);
    sun.position.set(8, 14, 6);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 30;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s;   sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(C.breeze, 0.25);
    fill.position.set(-6, 5, 4);
    this.scene.add(fill);

    // tint ground + fog by season
    const shift = { spring: { grass: 0x8FD166, fog: 0xC9EBD0 }, summer: { grass: C.grass, fog: C.breeze }, autumn: { grass: 0xC9B866, fog: 0xE6D6A8 }, winter: { grass: 0xD0E0D8, fog: 0xDAE6E8 } }[getSeason()];
    if (shift){
      this.scene.fog.color.setHex(shift.fog);
      this.scene.background.setHex(shift.fog);
    }
  }

  _buildGround(){
    // grass field — radius 48, segmented enough for the larger disc
    const grass = new THREE.Mesh(
      new THREE.CircleGeometry(GROUND_R, 72),
      new THREE.MeshLambertMaterial({ color: C.grass })
    );
    grass.rotation.x = -Math.PI/2;
    grass.receiveShadow = true;
    this.scene.add(grass);

    // radial plaza paving: two-tone stone rings + spokes so the compass reads from ground level
    const plazaGroup = new THREE.Group(); plazaGroup.rotation.x = -Math.PI/2; plazaGroup.position.y = 0.01;
    this.scene.add(plazaGroup);
    // outer ring
    const ringOuter = new THREE.Mesh(
      new THREE.RingGeometry(PLAZA_R - 1.2, PLAZA_R, 64),
      new THREE.MeshLambertMaterial({ color: ROAD_STONE })
    );
    plazaGroup.add(ringOuter);
    // inner ring
    const ringInner = new THREE.Mesh(
      new THREE.RingGeometry(4.5, 5.5, 64),
      new THREE.MeshLambertMaterial({ color: 0xD8C8B0 })
    );
    plazaGroup.add(ringInner);
    // four compass spokes
    for (let i = 0; i < 4; i++){
      const spoke = new THREE.Mesh(
        new THREE.PlaneGeometry(2.4, PLAZA_R),
        new THREE.MeshLambertMaterial({ color: 0xD8C8B0 })
      );
      spoke.position.set(0, PLAZA_R / 2, 0.005);
      spoke.rotation.z = i * Math.PI / 2;
      plazaGroup.add(spoke);
    }
    plazaGroup.children.forEach(c => { c.receiveShadow = true; });
  }

  // Plaza centre + rim furniture + wedge planting. Everything is arranged on the
  // compass so the town reads as planned rather than accreted.
  _buildPlaza(){
    // ---- central fountain + statue at (0,0) ----
    const fount = new THREE.Group(); this.scene.add(fount);
    cyl(3.2, 3.4, .35, C.crust, 0, .175, 0, fount, 28);        // outer basin rim
    cyl(2.6, 2.6, .2, 0x88C8D8, 0, .28, 0, fount, 24);         // water surface
    cyl(1.2, 1.4, .55, C.crust, 0, .45, 0, fount, 18);         // inner pedestal
    cyl(.18, .18, 1.6, C.crust, 0, 1.3, 0, fount, 10);         // central spout
    cyl(.45, .3, .2, 0x88C8D8, 0, 2.1, 0, fount, 14);          // top bowl

    // ---- lamp post down every spoke at a regular rhythm ----
    const lamp = (x, z) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      cyl(.12, .16, .3, C.crustDark, 0, .15, 0, g, 10);        // base
      cyl(.06, .06, 3.6, C.cocoa, 0, 1.9, 0, g, 8);            // post
      const globe = sphere(.24, 0xFFE9A8, 0, 3.85, 0, g, 12);
      globe.material = new THREE.MeshBasicMaterial({ color: 0xFFE9A8 });
      // point light so lamps actually glow at night
      const lit = new THREE.PointLight(0xFFE9A8, 0, 7, 1.8);
      lit.position.set(0, 3.8, 0); lit.name = 'streetlamp'; g.add(lit);
      return lit;
    };
    this.streetLamps = [];
    for (let a = 0; a < Math.PI * 2; a += Math.PI / 4){
      for (const r of [9, 17, 25]){
        const x = Math.sin(a) * r, z = -Math.cos(a) * r; // 0 = north (-z)
        const light = lamp(x, z);
        this.streetLamps.push({ light, x, z });
      }
    }

    // ---- benches on the plaza rim, between roads, facing the fountain ----
    const bench = (x, z) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      const ang = Math.atan2(x, z); // face toward (0,0)
      g.rotation.y = ang;
      box(1.6, .12, .5, C.rye, 0, .5, 0, g);              // seat
      box(1.6, .5, .12, C.rye, 0, .75, -.24, g);          // back
      box(.12, .5, .5, C.crustDark, -.7, .25, 0, g);      // legs
      box(.12, .5, .5, C.crustDark,  .7, .25, 0, g);
    };
    // place benches at the building angles (between roads) but only where a building exists
    BUILDINGS.forEach(b => bench(b.pos.x * 0.55, b.pos.z * 0.55));

    // ---- noticeboard (offset from the south road so it doesn't block the lane) ----
    const nbPos = new THREE.Vector3(4.0, 0, 8.5);
    const nb = new THREE.Group(); nb.position.copy(nbPos); this.scene.add(nb);
    nb.rotation.y = Math.atan2(nbPos.x, nbPos.z) + Math.PI; // face toward plaza
    box(1.4, 1.0, .12, C.crust, 0, .65, 0, nb);
    box(1.3, .9, .04, 0xF7F0E4, 0, .65, .06, nb);
    cyl(.06, .06, .7, C.crustDark, -.55, .35, 0, nb, 6);
    cyl(.06, .06, .7, C.crustDark,  .55, .35, 0, nb, 6);
    const nbSign = textSprite('📋 Noticeboard', 20, '#3A2A1C', 2.0, 0.7);
    nbSign.position.copy(nbPos.clone().setY(1.35)); this.scene.add(nbSign);

    // ---- wedge planting: flower beds / hedges / trees in every green gap ----
    const season = getSeason();
    const seasonPalette = {
      spring: { leaf: C.sprout, bloom: 0xF7C948, berry: 0xE9A6C4 },
      summer: { leaf: 0x5A9A3A, bloom: 0xFFFFFF, berry: 0xF49B33 },
      autumn: { leaf: 0xD98C4A, bloom: 0xE8461E, berry: 0x8B4513 },
      winter: { leaf: 0x7A9A8A, bloom: 0xE0F0F8, berry: 0xB0C8D8 }
    }[season];

    const flowerBed = (x, z, rot) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = rot; this.scene.add(g);
      box(1.2, .22, .45, C.crust, 0, .11, 0, g);
      for (let i = 0; i < 5; i++){
        const fx = -0.45 + i * 0.22;
        const col = i % 2 === 0 ? seasonPalette.bloom : seasonPalette.berry;
        sphere(.1, col, fx, .28, .08, g, 6);
        sphere(.08, seasonPalette.leaf, fx, .25, -.06, g, 6);
      }
    };
    const hedge = (x, z, rot) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = rot; this.scene.add(g);
      box(1.4, .7, .35, seasonPalette.leaf, 0, .45, 0, g);
      box(1.1, .5, .4, seasonPalette.leaf, 0, .55, 0, g);
    };
    const tree = (x, z, scale = 1) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(scale); this.scene.add(g);
      cyl(.14, .18, .8, C.rye, 0, .4, 0, g, 8);
      sphere(.9, seasonPalette.leaf, 0, 1.4, 0, g, 12);
      sphere(.55, seasonPalette.leaf, .35, 1.1, .25, g, 10);
      sphere(.55, seasonPalette.leaf, -.3, 1.2, -.2, g, 10);
    };

    // One cluster per 45° wedge, flanking buildings and filling reserved slots.
    for (let i = 0; i < 8; i++){
      const mid = i * 45 * (Math.PI / 180);                // wedge centre angle (0 = N)
      const r = 13.5;
      const x = Math.sin(mid) * r;
      const z = -Math.cos(mid) * r;
      // check if a building sits right in this wedge
      const buildingHere = BUILDINGS.find(b => Math.abs(((b.angle * Math.PI / 180) - mid + Math.PI * 2) % (Math.PI * 2)) < 0.2);
      if (buildingHere){
        // flank the building with two smaller plantings
        const fl = mid - 0.18, fr = mid + 0.18;
        flowerBed(Math.sin(fl) * 12.5, -Math.cos(fl) * 12.5, fl);
        flowerBed(Math.sin(fr) * 12.5, -Math.cos(fr) * 12.5, fr);
        hedge(Math.sin(mid) * 15.5, -Math.cos(mid) * 15.5, mid);
      } else {
        // reserved wedge: a tree + a small flower bed
        tree(x, z, 0.9);
        flowerBed(Math.sin(mid + 0.25) * 11, -Math.cos(mid + 0.25) * 11, mid + 0.25);
      }
    }
  }

  // Turn street lamps on at night / off during the day.
  _updateStreetLamps(){
    if (!this.streetLamps) return;
    const on = isNight() ? 0.9 : 0;
    this.streetLamps.forEach(l => { l.light.intensity = on; });
  }

  _buildBuildings(){
    const state = getState();
    BUILDINGS.forEach(b => {
      const level = state.buildings[b.id]?.level ?? 0;
      const g = new THREE.Group();
      g.position.copy(b.pos);
      // face the plaza center
      g.lookAt(0, 0, 0);
      this.scene.add(g);

      if (b.id === 'grill') this._buildGrillExterior(g, b, level);
      else if (b.id === 'council') this._buildCouncilExterior(g, b, level);
      else this._buildStorefront(g, b, level);

      // door trigger sits at the front face, in world space
      const doorPos = b.pos.clone().add(
        new THREE.Vector3(0, 0, 0).sub(b.pos).setLength(2.8).setY(0)
      );
      const sign = textSprite(b.name, 30, '#6B4A2F', 3.6, 1.0);
      sign.position.copy(b.pos.clone().setY(4.6));
      this.scene.add(sign);

      // closed sign for shops that are currently shut
      const closedSign = textSprite('CLOSED', 26, '#E23B4E', 2.4, 0.7);
      closedSign.position.copy(b.pos.clone().setY(3.2));
      closedSign.visible = false;
      closedSign.name = 'closed-sign-' + b.id;
      this.scene.add(closedSign);

      this.doors.push({ building: b, doorPos, level, group: g, closedSign });
    });
  }

  _buildCouncilExterior(g, b, level){
    g.clear();
    const body = box(4.6, 4.2, 4.2, b.color, 0, 2.1, 0, g);   // stone civic building
    body.castShadow = true;
    box(4.8, .45, 4.4, 0x6E5E4E, 0, 4.3, 0, g);                // roof trim
    box(1.6, 2.6, .2, 0x4A3E32, 0, 1.3, 2.1, g);               // door
    box(.22, .22, .28, 0xFFD700, 0, 2.6, 2.15, g);             // doorknob
    // columns
    cyl(.22, .22, 3.6, 0xC8B8A0, -1.5, 1.8, 2.0, g, 10);
    cyl(.22, .22, 3.6, 0xC8B8A0,  1.5, 1.8, 2.0, g, 10);
    // windows
    box(1.0, 1.4, .1, 0xB8D8E8, -1.3, 2.8, 2.1, g);
    box(1.0, 1.4, .1, 0xB8D8E8,  1.3, 2.8, 2.1, g);
    box(1.0, 1.4, .1, 0xB8D8E8, -1.3, 2.8, -2.1, g);
    box(1.0, 1.4, .1, 0xB8D8E8,  1.3, 2.8, -2.1, g);
    // flag on top
    cyl(.04, .04, 1.3, 0x444444, 1.5, 4.9, 1.5, g, 6);
    box(.7, .45, .06, 0xE23B4E, 1.85, 5.35, 1.5, g);
    // upgrade flourishes
    if (level >= 2){
      box(5.0, .35, .6, b.color, 0, 2.4, 2.3, g); // front steps awning
    }
    if (level >= 3){
      cyl(.25, .25, 1.8, C.cocoa, -2.1, 3.8, 0, g, 8);
      const flag = box(1.2, .7, .06, b.color, -2.6, 4.5, 0, g);
    }
  }

  _buildGrillExterior(g, b, level){
    g.clear();
    const built = level >= 1;
    const bodyColor = built ? b.color : 0x9A8C74;
    const w = 4.4, h = 3.2;
    box(w, h, 4, bodyColor, 0, h/2, 0, g);
    const roof = box(w + .6, .5, 4.6, C.crustDark, 0, h + .2, 0, g);
    roof.castShadow = true;
    // big chimney with a little smoke puff
    cyl(0.22, 0.28, 1.6, 0x5A4A4A, 1.2, h + 0.8, 0, g, 10);
    cyl(0.32, 0.32, 0.12, 0x3A3A3A, 1.2, h + 1.7, 0, g, 10);
    // door and windows
    box(1.2, 2, .2, C.crustDark, 0, 1, 2.0, g);
    if (built){
      box(.9, .9, .15, 0xFFCC88, -1.3, 1.6, 2.0, g); // warm lit windows
      box(.9, .9, .15, 0xFFCC88,  1.3, 1.6, 2.0, g);
    }
    if (level >= 2){
      box(w + .4, .3, 1.2, b.color, 0, 2.4, 2.4, g);
    }
  }

  _buildStorefront(g, b, level){
    g.clear();   // rebuild-safe if an existing building is upgraded in place
    // level 0 = undiscovered shell (grey, boarded). level 1+ = colored + signage.
    const built = level >= 1;
    const bodyColor = built ? b.color : 0x9A8C74;
    const w = 4 + level * 0.4;
    const h = 3 + level * 0.5;

    box(w, h, 4, bodyColor, 0, h/2, 0, g);            // main block
    // roof
    const roof = box(w + .6, .5, 4.6, C.crustDark, 0, h + .2, 0, g);
    roof.castShadow = true;
    // door
    box(1.2, 2, .2, C.crustDark, 0, 1, 2.0, g);
    // windows appear once built
    if (built){
      box(.9, .9, .15, C.breeze, -1.3, 1.6, 2.0, g);
      box(.9, .9, .15, C.breeze,  1.3, 1.6, 2.0, g);
    }
    // awning / upgrade flourishes at higher levels
    if (level >= 2){
      box(w + .4, .3, 1.2, b.color, 0, 2.4, 2.4, g);
    }
    if (level >= 3){
      cyl(.2, .2, 1.5, C.cocoa, -w/2 + .3, h + 1, 0, g, 8);
      const flag = box(1, .6, .05, b.color, -w/2 + .8, h + 1.4, 0, g);
    }
  }

  // ---- shared road builder: one paving material, arch + banner at each gate ----
  _buildRoad(angleDeg, label, sceneId, signColor = '#6B4A2F'){
    const a = angleDeg * Math.PI / 180;
    const dx = Math.sin(a), dz = -Math.cos(a); // 0° = north (-z)
    const startR = PLAZA_R - 0.4;
    const endR = GATE_R;
    const midR = (startR + endR) / 2;
    const length = endR - startR;
    const width = 3.2;

    // road segment from plaza edge to gate
    const path = new THREE.Mesh(
      new THREE.PlaneGeometry(width, length),
      new THREE.MeshLambertMaterial({ color: ROAD_STONE })
    );
    path.rotation.x = -Math.PI/2;
    path.rotation.z = -a; // align with spoke
    path.position.set(dx * midR, 0.02, dz * midR);
    path.receiveShadow = true;
    this.scene.add(path);

    // archway at the gate
    const gx = dx * endR, gz = dz * endR;
    const perpX = Math.cos(a), perpZ = Math.sin(a);
    const halfW = width / 2 + 0.4;
    cyl(0.18, 0.18, 3.6, C.rye, gx - perpX * halfW, 1.8, gz - perpZ * halfW, this.scene, 10);
    cyl(0.18, 0.18, 3.6, C.rye, gx + perpX * halfW, 1.8, gz + perpZ * halfW, this.scene, 10);
    const beam = box(length * 0.26, 0.45, 0.35, C.rye, gx, 3.55, gz, this.scene);
    beam.rotation.y = -a;

    const sign = textSprite(label, 28, signColor, 4.2, 0.9);
    sign.position.set(gx, 4.2, gz); sign.rotation.y = a;
    this.scene.add(sign);

    // door trigger just inside the arch
    const doorR = endR - 2.2;
    this.doors.push({
      building: { name: label.replace(/[▸◂]/g, '').trim(), scene: sceneId },
      doorPos: new THREE.Vector3(dx * doorR, 0, dz * doorR),
      level: 1
    });
  }

  _buildDowntownRoad(){  this._buildRoad(180, 'Downtown ▸',       'downtown'); }
  _buildFarmRoad(){      this._buildRoad(135, 'Sunnybrook Farm ▸','farm'); }
  _buildParkRoad(){      this._buildRoad(0,   '◂ Toast Park',     'park', '#3E5A2A'); }
  _buildUptownRoad(){    this._buildRoad(45,  'Uptown ▸',         'uptown'); }
  _buildBeachRoad(){     this._buildRoad(315, 'Sandy Toes Beach ▸','beach', '#5A4A2A'); }
  _buildBananaRoad(){    this._buildRoad(90,  'Banana Village ▸', 'bananaVillage'); }
  _buildForestRoad(){    this._buildRoad(270, '◂ The Forest',     'forest', '#3E5A2A'); }
  _buildToasterRoad(){   this._buildRoad(225, 'The Infinite Toaster ▸', 'infiniteToaster', '#FFCC66'); }

  // Decor bought through Mayor Muffin's furniture tab.
  _buildTownFurniture(){
    if (getFlag('townFurniture_flowers')){
      const season = getSeason();
      const bloom = { spring: 0xF7C948, summer: 0xFFFFFF, autumn: 0xE8461E, winter: 0xE0F0F8 }[season];
      const spots = [[-5.5, -5.5], [5.5, -5.5], [-5.5, 5.5], [5.5, 5.5]];
      spots.forEach(([x, z]) => {
        const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
        box(.6, .35, .6, C.crust, 0, .175, 0, g);
        sphere(.35, C.sprout, 0, .55, 0, g, 10);
        sphere(.2, C.sproutDim, .2, .48, .18, g, 8);
        sphere(.2, bloom, -.15, .55, .15, g, 8);
      });
    }
    if (getFlag('townFurniture_bench')){
      this._bench(new THREE.Vector3(-9.5, 0, 0), Math.PI / 2);
    }
    if (getFlag('townFurniture_lamp')){
      const g = new THREE.Group(); g.position.set(0, 0, -9.5); this.scene.add(g);
      cyl(.08, .08, 2.8, C.cocoa, 0, 1.4, 0, g, 8);
      box(.5, .35, .15, C.cocoa, 0, 2.8, 0, g);
      sphere(.12, 0xFFEEA8, 0, 2.6, .1, g, 8);
    }
  }

  _bench(pos, rot){
    const g = new THREE.Group(); g.position.copy(pos); g.rotation.y = rot; this.scene.add(g);
    box(1.5, .1, .45, C.crustDark, 0, .5, 0, g);
    box(1.5, .35, .08, C.crustDark, 0, .7, -.2, g);
    cyl(.05, .05, .45, 0x5A4A2A, -.6, .22, 0, g, 6);
    cyl(.05, .05, .45, 0x5A4A2A,  .6, .22, 0, g, 6);
  }

  _makeKingBread(){
    const SPIRIT = 0xE8C24B;
    const build = (g) => {
      const body = new THREE.Mesh(new THREE.SphereGeometry(0.34, 14, 14), new THREE.MeshLambertMaterial({ color: SPIRIT, transparent: true, opacity: 0.9 }));
      body.position.y = 1.05; g.add(body);
      box(0.65, 0.75, 0.3, SPIRIT, 0, 0.52, 0, g);
      sphere(0.05, 0xFFF4DF, -0.13, 1.12, 0.22, g, 8);
      sphere(0.05, 0xFFF4DF,  0.13, 1.12, 0.22, g, 8);
      const crown = new THREE.Group(); crown.position.set(0, 1.48, 0); g.add(crown);
      cyl(0.24, 0.26, 0.14, 0xFFD700, 0, 0, 0, crown, 12);
      for (let i = 0; i < 5; i++){
        const a = (i - 2) * 0.35;
        const p = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.16, 6), new THREE.MeshBasicMaterial({ color: 0xFFD700 }));
        p.position.set(Math.sin(a) * 0.22, 0.14, Math.cos(a) * 0.22); crown.add(p);
      }
      cyl(0.03, 0.03, 1.4, 0xFFD700, 0.4, 0.85, 0.2, g, 6).rotation.z = -0.2;
      sphere(0.08, 0xFFD700, 0.52, 1.55, 0.32, g, 10);
    };
    return new Townsperson({
      name: 'Baker King', line: 'the Golden Loaf shines on Toast Town once more.',
      pos: new THREE.Vector3(7.5, 0, 0), facing: -Math.PI / 2,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _buildBakerKingStatue(){
    this.bakerKingStatueGroup = new THREE.Group();
    this.bakerKingStatueGroup.position.copy(this.statuePos);
    this.bakerKingStatueGroup.visible = getFlag('bakerKingStatuePlaced');
    this.scene.add(this.bakerKingStatueGroup);

    // pedestal rising from the fountain
    cyl(1.1, 1.1, 0.9, 0x8A7A6A, 0, 0.45, 0, this.bakerKingStatueGroup, 20);
    box(0.9, 0.1, 0.9, 0xE8C24B, 0, 0.95, 0, this.bakerKingStatueGroup);
    // loaf-shaped king
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.55, 16, 16), new THREE.MeshLambertMaterial({ color: 0xC9A05C }));
    body.position.y = 1.65; body.scale.set(1, 1.25, 1); this.bakerKingStatueGroup.add(body);
    // crown
    const crown = new THREE.Group(); crown.position.set(0, 2.35, 0); this.bakerKingStatueGroup.add(crown);
    cyl(0.32, 0.35, 0.18, 0xFFD700, 0, 0, 0, crown, 12);
    for (let i = 0; i < 5; i++){
      const a = (i - 2) * 0.35;
      const p = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.22, 6), new THREE.MeshBasicMaterial({ color: 0xFFD700 }));
      p.position.set(Math.sin(a) * 0.3, 0.2, Math.cos(a) * 0.3); crown.add(p);
    }
    // scepter
    cyl(0.05, 0.05, 1.6, 0xFFD700, 0.55, 1.3, 0.25, this.bakerKingStatueGroup, 6).rotation.z = -0.25;
    sphere(0.14, 0xFFD700, 0.65, 2.1, 0.4, this.bakerKingStatueGroup, 10);
    // plaque
    const plaque = textSprite('Baker King', 20, '#3A2A1C', 2.0, 0.6);
    plaque.position.set(0, 0.65, 0.6); this.bakerKingStatueGroup.add(plaque);
  }

  _receiveBlessing(){
    if (!isActive('pilgrimage-to-king')){
      HUD.say('The statue glows warmly. The Baker King watches over Toast Town.');
      return;
    }
    if (getFlag('visitedKingStatueToday')){
      HUD.say('You have already received today\'s blessing. Return tomorrow.');
      return;
    }
    setFlag('visitedKingStatueToday', true);
    flagObjective('pilgrimage-to-king', 'visitedKingStatueToday');
    const reward = turnIn('pilgrimage-to-king');
    HUD.refreshCrumbs();
    HUD.say('The Baker King blesses you with crumbs from the Golden Loaf!');
    this.bread.drawFace('yum');
  }

  // Dense tree line around the world edge so it feels enclosed rather than a disc in void.
  _buildPerimeter(){
    const season = getSeason();
    const leaf = { spring: C.sprout, summer: 0x3A7A2E, autumn: 0xC98C3E, winter: 0x6A8A7A }[season];
    const count = 56;
    for (let i = 0; i < count; i++){
      const a = (i / count) * Math.PI * 2;
      // leave gaps for the 8 roads so trees don't block the arches
      const deg = (a * 180 / Math.PI + 360) % 360;
      const nearRoad = [0, 45, 90, 135, 180, 225, 270, 315].some(road => Math.abs(((deg - road + 180) % 360) - 180) < 9);
      if (nearRoad) continue;
      const r = 43 + Math.random() * 3.5;
      const x = Math.sin(a) * r;
      const z = -Math.cos(a) * r;
      const scale = 0.85 + Math.random() * 0.5;
      const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(scale); this.scene.add(g);
      cyl(.16, .2, 1.0, C.rye, 0, .5, 0, g, 8);
      sphere(1.1, leaf, 0, 1.8, 0, g, 12);
      sphere(.7, leaf, .35, 1.4, .25, g, 10);
      sphere(.7, leaf, -.3, 1.5, -.2, g, 10);
    }
  }

  // ---- scene lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.75];
    this.input.onTap = (ndc, ray) => this._onTap(ray);

    HUD.setSubtitle('the town square');
    HUD.refreshCrumbs();
    HUD.refreshLumber();
    HUD.refreshQuests();
    const st = getWeatherState();
    HUD.say(`Welcome to Toast Town! ${weatherIcon(st.weather)} ${weatherName(st.weather)} · ${st.temp}°C · ${st.season}. Click anywhere to walk.`);
    HUD.setHint('click anywhere to walk · drag to look around · walk up to folks & doors');
    HUD.hideHintSoon();
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.toggleQuestScroll(false); this._clearPanels(); }

  _onTap(ray){
    // First check if the player clicked an NPC
    if (checkNpcClick(ray, [...this.npcs, this.mayor], this.bread)) return;

    // Free roam: walk to wherever you click on the ground. Entering a building
    // happens by proximity (walk up to its door), not by clicking the building.
    // Intersect the click ray with the ground plane (y = 0) so clicking on grass,
    // plaza, or even over a building all resolve to a real ground point.
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;               // looking at the horizon
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;                                // ground is behind the camera
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);

    if (gp.length() < 3.2) return;                     // don't wade into the central fountain
    if (gp.length() > GROUND_R - 2) gp.setLength(GROUND_R - 2); // stay on the grass

    HUD.hideHint();
    this.bread.walkTo(gp, () => {});
  }

  update(dt){
    const phase = getPhase();
    this.ambient.update(dt, phase);
    this.weather.update(dt, this.bread.root.position);
    this._updateStreetLamps();

    // update closed-sign visibility for shops with hours
    this.doors.forEach(d => {
      if (!d.closedSign) return;
      const hours = d.building.hours ?? 'always';
      d.closedSign.visible = d.level >= 1 && hours !== 'always' && !isOpen(hours);
    });

    // joystick movement overrides tap-to-walk
    if (this.input.moving){
      this.bread.stop();
      const m = this.input.move;
      // camera-relative: forward is toward -Z rotated by azimuth
      const ang = this.azimuth;
      const fwd = new THREE.Vector3(Math.sin(ang), 0, Math.cos(ang));
      const right = new THREE.Vector3(Math.cos(ang), 0, -Math.sin(ang));
      const dir = new THREE.Vector3()
        .addScaledVector(right, m.x)
        .addScaledVector(fwd, -m.y);
      if (dir.lengthSq() > 0.001){
        dir.normalize();
        this.bread.facing = Math.atan2(dir.x, dir.z);
        this.bread.root.rotation.y = this.bread.facing;
        const next = this.bread.root.position.clone().addScaledVector(dir, this.bread.speed * dt);
        if (next.length() < GROUND_R - 2) this.bread.root.position.copy(next);
        this.bread._animateWalk(dt);
      }
    } else {
      this.bread.update(dt);
    }

    // townsfolk wander + greet when Bread Boi comes near; hide/show by schedule
    this.npcs.forEach(n => {
      const visible = n.shouldSpawn();
      if (n.root.visible !== visible) n.root.visible = visible;
      if (visible) n.update(dt, this.bread.root.position);
    });
    this.mayor.update(dt, this.bread.root.position);

    // Baker King spirit + statue appear once the ceremony is complete
    const kingVisible = !!getFlag('bakerKingBlessing');
    if (this.kingBread && this.kingBread.root.visible !== kingVisible) this.kingBread.root.visible = kingVisible;
    if (this.kingBread && kingVisible) this.kingBread.update(dt, this.bread.root.position);
    if (this.bakerKingStatueGroup) this.bakerKingStatueGroup.visible = getFlag('bakerKingStatuePlaced');

    // proximity to doors / quest-giver / statue → enter prompt
    let near = null, nd = Infinity;
    this.doors.forEach(d => {
      const dist = d.doorPos.distanceTo(this.bread.root.position);
      if (dist < nd){ nd = dist; near = { kind: 'door', data: d, dist }; }
    });
    const mayorDist = this.mayor.root.position.distanceTo(this.bread.root.position);
    if (mayorDist < 2.6 && mayorDist < nd){
      near = { kind: 'mayor', data: this.mayor, dist: mayorDist };
      nd = mayorDist;
    }
    if (this.bakerKingStatueGroup && this.bakerKingStatueGroup.visible){
      const statueDist = this.statuePos.distanceTo(this.bread.root.position);
      const label = isActive('pilgrimage-to-king') && !getFlag('visitedKingStatueToday')
        ? '🍞 receive the Baker King\'s blessing'
        : '🍞 visit the Baker King statue';
      if (statueDist < 2.4 && statueDist < nd){
        near = { kind: 'statue', data: 'statue', label, dist: statueDist };
        nd = statueDist;
      }
    }
    if (near && nd < 2.6){
      if (this.nearTarget !== near.data){
        this.nearTarget = near.data;
        if (near.kind === 'mayor'){
          HUD.showEnter('🗣 talk to Mayor Muffin', () => this._showMayorDialog());
        } else if (near.kind === 'statue'){
          HUD.showEnter(near.label, () => this._receiveBlessing());
        } else {

        const d = near.data;
        const hours = d.building.hours ?? 'always';
        const isOpenNow = d.level >= 1 && isOpen(hours);

        if (d.level < 1){
          HUD.showEnter(`${d.building.name} — coming soon`, () => {
            HUD.say("this building isn't open yet — earn crumbs to help build it!");
          });
        } else if (isOpenNow){
          HUD.showEnter(`enter ${d.building.name}`, () => {
            this.manager.goTo(d.building.scene, { from: 'town' });
          });
        } else {
          const label = openLabel(hours);
          HUD.showEnter(`${d.building.name} — closed (${label})`, () => {
            HUD.say(`${d.building.name} is closed right now. Open hours: ${label}.`);
          });
        }

        }
      }
    } else if (this.nearTarget){
      this.nearTarget = null;
      HUD.showEnter(null);
    }

    // camera smoothing + follow Bread Boi
    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(
      new THREE.Vector3(this.bread.root.position.x * 0.5, 1.4, this.bread.root.position.z * 0.5),
      0.06
    );
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  _showMayorDialog(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.mayor.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderMayorDialog();
  }

  _renderMayorDialog(){
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Mayor Muffin</h2>
        <div class="cafe-sub">mayor of Toast Town</div>
        <div class="q-tabs">
          <button class="cafe-btn ghost ${this.mayorTab === 'quests' ? 'active' : ''}" data-tab="quests">📜 quests</button>
          <button class="cafe-btn ghost ${this.mayorTab === 'town' ? 'active' : ''}" data-tab="town">🏘 buildings</button>
          <button class="cafe-btn ghost ${this.mayorTab === 'furniture' ? 'active' : ''}" data-tab="furniture">🪑 furniture</button>
        </div>
        <div class="q-tab-body">${this.mayorTab === 'quests' ? this._mayorQuestRows() : this.mayorTab === 'town' ? this._mayorTownRows() : this._mayorFurnitureRows()}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('[data-tab]').forEach(b => b.onclick = () => { this.mayorTab = b.dataset.tab; this._renderMayorDialog(); });
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderMayorDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelectorAll('.upgrade').forEach(b => b.onclick = () => this._onUpgradeBuilding(b.dataset.id));
    p.querySelectorAll('.buy-furn').forEach(b => b.onclick = () => this._onBuyFurniture(b.dataset.id));
    p.querySelector('.close').onclick = () => { this._clearPanels(); this.nearTarget = null; };
  }

  _mayorQuestRows(){
    const { available, active } = questsForGiver('mayor');
    const rows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        rows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        const label = q.objective.kind === 'flag' ? q.objective.noun : `${p.have} / ${p.goal} ${q.objective.noun}`;
        rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${label}</span></div>
          <span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    return rows.length ? rows.join('')
      : `<p class="q-dnone">"No new decrees today, Bread Boi. You're doing great out there!"</p>`;
  }

  _mayorTownRows(){
    const rows = BUILDINGS.map(b => {
      const d = this.doors.find(door => door.building.id === b.id);
      const level = d ? d.level : (getState().buildings[b.id]?.level ?? 0);
      const canUpgrade = level < MAX_LEVEL;
      const cost = UPGRADE_COST[level] ?? 0;
      const affordable = getCrumbs() >= cost;
      if (!canUpgrade){
        return `<div class="q-drow"><div><b>${b.name}</b><span>level ${level} — maxed out!</span></div><span class="q-dpend">✓</span></div>`;
      }
      const label = affordable ? `level ${level} → ${level + 1}` : `level ${level} → ${level + 1} (need ${cost} 🍞)`;
      return `<div class="q-drow"><div><b>${b.name}</b><span>${label}</span></div>
        <button class="cafe-btn upgrade ${affordable ? '' : 'disabled'}" data-id="${b.id}">${affordable ? 'upgrade' : 'too pricey'}</button></div>`;
    });
    return rows.join('');
  }

  _mayorFurnitureRows(){
    const items = [
      { id: 'townFurniture_flowers', name: 'plaza flower boxes', icon: '🌷', cost: 50, desc: 'four planters around the fountain' },
      { id: 'townFurniture_bench', name: 'south-side bench', icon: '🪑', cost: 40, desc: 'a new place to rest by the forest road' },
      { id: 'townFurniture_lamp', name: 'ornate plaza lamp', icon: '🏮', cost: 60, desc: 'a tall lamp to light the north path' }
    ];
    const rows = items.map(it => {
      const owned = getFlag(it.id);
      const affordable = getCrumbs() >= it.cost;
      if (owned) return `<div class="q-drow"><div><b>${it.icon} ${it.name}</b><span>${it.desc}</span></div><span class="q-dpend">✓ placed</span></div>`;
      return `<div class="q-drow"><div><b>${it.icon} ${it.name}</b><span>${it.desc}<br>🍞 ${it.cost}</span></div>
        <button class="cafe-btn buy-furn ${affordable ? '' : 'disabled'}" data-id="${it.id}">${affordable ? 'buy' : 'too pricey'}</button></div>`;
    });
    return rows.join('') + '<p class="q-dnone">Furniture is placed instantly and persists forever.</p>';
  }

  _onBuyFurniture(id){
    const costs = { townFurniture_flowers: 50, townFurniture_bench: 40, townFurniture_lamp: 60 };
    const cost = costs[id];
    if (!cost || getFlag(id)) return;
    if (getCrumbs() < cost){ HUD.say(`you need ${cost} crumbs for that.`); return; }
    spendCrumbs(cost);
    setFlag(id, true);
    this._buildTownFurniture();
    HUD.refreshCrumbs();
    HUD.say('New town furniture delivered!');
    this._renderMayorDialog();
  }

  _onUpgradeBuilding(id){
    const d = this.doors.find(door => door.building.id === id);
    if (!d) return;
    const cost = UPGRADE_COST[d.level] ?? 0;
    if (getCrumbs() < cost){
      HUD.say(`you need ${cost} crumbs to upgrade ${d.building.name}.`);
      return;
    }
    spendCrumbs(cost);
    upgradeBuilding(id);
    d.level++;
    this._buildStorefront(d.group, d.building, d.level);
    setFlag('upgradedBuilding', true);
    flagObjective('town-booster', 'upgradedBuilding');
    HUD.refreshCrumbs();
    HUD.say(`${d.building.name} is now level ${d.level}! bigger and toastier.`);
    this._renderMayorDialog();
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    HUD.refreshLumber();
    this.bread.drawFace('yum');
    this._renderMayorDialog();
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }
}
